import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import SearchableSelect from "@/components/SearchableSelect";
import ColumnFilter, { SortableHeader } from "@/components/ColumnFilter";
import { DownloadSimple, Package, Truck } from "@phosphor-icons/react";

const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const firstOfMonth = () => { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth(), 1)); };

const fmtDate = (s) => {
  const d = new Date(s || "");
  if (Number.isNaN(d.getTime())) return "—";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const PAGE_SIZE = 100;

export default function PurchaseSummary() {
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(iso(new Date()));
  const [supplierId, setSupplierId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [data, setData] = useState({ rows: [], total_amount: 0, total_quantity: 0, total_entries: 0, facets: {} });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [colFilters, setColFilters] = useState({
    created_at_day: new Set(), supplier_name: new Set(), category_name: new Set(),
    product_sku: new Set(), product_name: new Set(), unit_of_measure: new Set(),
    type: new Set(),
  });
  const setColFilter = (k) => (v) => setColFilters((f) => ({ ...f, [k]: v }));

  const baseParams = () => {
    const qs = new URLSearchParams();
    if (dateFrom) qs.set("date_from", dateFrom);
    if (dateTo) qs.set("date_to", dateTo);
    if (supplierId) qs.set("supplier_id", supplierId);
    if (warehouseId) qs.set("warehouse_id", warehouseId);
    qs.set("sort_by", sort.key);
    qs.set("sort_dir", sort.dir);
    Object.entries(colFilters).forEach(([k, set]) => set.forEach((v) => qs.append(`f_${k}`, v)));
    return qs;
  };
  const reqId = useRef(0);

  useEffect(() => {
    Promise.all([api.get("/suppliers"), api.get("/warehouses")])
      .then(([s, w]) => { setSuppliers(s.data || []); setWarehouses(w.data || []); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = baseParams();
    qs.set("skip", "0"); qs.set("limit", String(PAGE_SIZE));
    const id = ++reqId.current;
    api.get(`/purchase-summary?${qs.toString()}`)
      .then((r) => { if (id === reqId.current) setData(r.data); })
      .catch((e) => { if (id === reqId.current) toast.error(formatApiError(e)); })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  }, [dateFrom, dateTo, supplierId, warehouseId, sort.key, sort.dir, colFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = async () => {
    if (loadingMore || data.rows.length >= data.total_entries) return;
    setLoadingMore(true);
    const id = reqId.current;
    try {
      const qs = baseParams();
      qs.set("skip", String(data.rows.length)); qs.set("limit", String(PAGE_SIZE));
      const res = await api.get(`/purchase-summary?${qs.toString()}`);
      if (id !== reqId.current) return;
      setData((prev) => ({ ...res.data, facets: prev.facets, rows: [...prev.rows, ...(res.data.rows || [])] }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingMore(false); }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const t = localStorage.getItem("token");
      const qs = baseParams();
      const res = await fetch(`${API_BASE}/purchase-summary/export?${qs.toString()}`, {
        credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const url = window.URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url; a.download = `riepilogo-acquisti_${dateFrom || "inizio"}_${dateTo || "oggi"}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Riepilogo esportato");
    } catch (e) { toast.error(formatApiError(e) || "Errore durante l'export"); }
    finally { setExporting(false); }
  };

  // Column filters are applied server-side so totals always reflect the filtered table.
  const facets = data.facets || {};
  const uniques = Object.fromEntries(Object.keys(colFilters).map((k) => [k, { all: facets[k] || [] }]));
  const filtered = data.rows;
  const hasCol = Object.values(colFilters).some((s) => s.size > 0);
  const displayTotal = { amount: data.total_amount, qty: data.total_quantity, entries: data.total_entries };

  const supplierOpts = useMemo(() => [{ value: "", label: "Tutti i fornitori" },
    ...suppliers.map((s) => ({ value: s.id, label: s.name, keywords: [s.name] }))], [suppliers]);
  const warehouseOpts = useMemo(() => [{ value: "", label: "Tutti i magazzini" },
    ...warehouses.map((w) => ({ value: w.id, label: w.name, sublabel: w.type === "mobile" ? "Furgone" : "Fisico", keywords: [w.name] }))], [warehouses]);

  return (
    <div>
      <PageHeader
        eyebrow="Approvvigionamento"
        title="Riepilogo Acquisti"
        subtitle="Entrate e resi a fornitore nel periodo. I totali seguono tutti i filtri, anche quelli di colonna."
        action={
          <button data-testid="ps-export-btn" onClick={exportXlsx} disabled={exporting} className="text-sm px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] disabled:opacity-60 text-white transition-colors flex items-center gap-2">
            <DownloadSimple size={16} weight="bold" /> <span>{exporting ? "Esporto..." : "Esporta Excel"}</span>
          </button>
        }
      />
      <div className="p-4 md:p-8 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 bg-white border border-neutral-200 p-4">
          <label className="block">
            <span className="label-eyebrow block mb-2">Dal</span>
            <input type="date" data-testid="ps-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 text-sm" />
          </label>
          <label className="block">
            <span className="label-eyebrow block mb-2">Al</span>
            <input type="date" data-testid="ps-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 text-sm" />
          </label>
          <div>
            <span className="label-eyebrow block mb-2">Fornitore</span>
            <SearchableSelect testid="ps-supplier" value={supplierId} options={supplierOpts} onChange={setSupplierId} placeholder="Tutti i fornitori" />
          </div>
          <div>
            <span className="label-eyebrow block mb-2">Magazzino</span>
            <SearchableSelect testid="ps-warehouse" value={warehouseId} options={warehouseOpts} onChange={setWarehouseId} placeholder="Tutti i magazzini" />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Stat testid="ps-total-amount" label={hasCol ? "Totale filtrato" : "Totale acquistato"} value={eur(displayTotal.amount)} strong />
          <Stat testid="ps-total-qty" label="Quantità netta" value={displayTotal.qty.toLocaleString("it-IT")} />
          <Stat testid="ps-total-entries" label="Movimenti" value={String(displayTotal.entries)} />
        </div>

        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="ps-table">
            <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0 z-10">
              <tr className="text-left">
                <Th>
                  <SortableHeader testid="ps-sort-date" label="Data" sortKey="created_at" sortConfig={sort} setSortConfig={setSort} />
                  <ColumnFilter testid="ps-filter-date" values={uniques.created_at_day.all} selected={colFilters.created_at_day} onChange={setColFilter("created_at_day")} />
                </Th>
                <Th>
                  <SortableHeader testid="ps-sort-type" label="Tipo" sortKey="type" sortConfig={sort} setSortConfig={setSort} />
                  <ColumnFilter testid="ps-filter-type" values={uniques.type.all.map((v) => ({ value: v, label: v === "reso_fornitore" ? "Reso a fornitore" : "Acquisto" }))} selected={colFilters.type} onChange={setColFilter("type")} />
                </Th>
                <Th>
                  <SortableHeader testid="ps-sort-supplier" label="Fornitore" sortKey="supplier_name" sortConfig={sort} setSortConfig={setSort} />
                  <ColumnFilter testid="ps-filter-supplier" values={uniques.supplier_name.all} selected={colFilters.supplier_name} onChange={setColFilter("supplier_name")} />
                </Th>
                <Th>
                  <SortableHeader testid="ps-sort-category" label="Categoria apparato" sortKey="category_name" sortConfig={sort} setSortConfig={setSort} />
                  <ColumnFilter testid="ps-filter-category" values={uniques.category_name.all} selected={colFilters.category_name} onChange={setColFilter("category_name")} />
                </Th>
                <Th>
                  <SortableHeader testid="ps-sort-sku" label="SKU" sortKey="product_sku" sortConfig={sort} setSortConfig={setSort} />
                  <ColumnFilter testid="ps-filter-sku" values={uniques.product_sku.all} selected={colFilters.product_sku} onChange={setColFilter("product_sku")} />
                </Th>
                <Th>
                  <SortableHeader testid="ps-sort-product" label="Prodotto" sortKey="product_name" sortConfig={sort} setSortConfig={setSort} />
                  <ColumnFilter testid="ps-filter-product" values={uniques.product_name.all} selected={colFilters.product_name} onChange={setColFilter("product_name")} />
                </Th>
                <Th className="text-right">
                  <SortableHeader testid="ps-sort-qty" label="Quantità" sortKey="quantity" sortConfig={sort} setSortConfig={setSort} />
                  <ColumnFilter testid="ps-filter-uom" values={uniques.unit_of_measure.all} selected={colFilters.unit_of_measure} onChange={setColFilter("unit_of_measure")} />
                </Th>
                <Th className="text-right">
                  <SortableHeader testid="ps-sort-price" label="Prezzo unit." sortKey="unit_price" sortConfig={sort} setSortConfig={setSort} />
                </Th>
                <Th className="text-right">
                  <SortableHeader testid="ps-sort-total" label="Totale" sortKey="line_total" sortConfig={sort} setSortConfig={setSort} />
                </Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="9" className="px-4 py-12 text-center text-neutral-500" data-testid="ps-empty">
                  {loading ? "Caricamento..." : "Nessun movimento di acquisto nel periodo selezionato."}
                </td></tr>
              ) : filtered.map((r) => {
                const isReturn = r.type === "reso_fornitore";
                return (
                <tr key={r.movement_id} data-testid={`ps-row-${r.movement_id}`} className={`border-b border-neutral-100 hover:bg-neutral-50 transition-colors ${isReturn ? "bg-amber-50/40" : ""}`}>
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-neutral-700">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3 text-xs">
                    {isReturn ? (
                      <span data-testid={`ps-type-reso-${r.movement_id}`} className="inline-block px-1.5 py-0.5 bg-amber-200 text-amber-900 uppercase tracking-wider text-[10px]">Reso a fornitore</span>
                    ) : (
                      <span className="inline-block px-1.5 py-0.5 bg-emerald-100 text-emerald-800 uppercase tracking-wider text-[10px]">Acquisto</span>
                    )}
                  </td>
                  <td className="px-4 py-3"><div className="flex items-center gap-1.5"><Truck size={14} className="text-neutral-400 shrink-0" />{r.supplier_name}</div></td>
                  <td className="px-4 py-3 text-xs">{r.category_name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.product_sku}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Package size={14} className="text-neutral-400 shrink-0" />
                      <span>{r.product_name}</span>
                      {r.is_serialized === false && <span className="text-[10px] bg-neutral-200 text-neutral-700 px-1.5 py-0.5 uppercase tracking-wider">Bulk</span>}
                    </div>
                  </td>
                  <td className={`px-4 py-3 text-right font-mono ${isReturn ? "text-amber-700" : ""}`}>{r.quantity > 0 ? `+${r.quantity}` : r.quantity} <span className="text-neutral-400 text-xs">{r.unit_of_measure}</span></td>
                  <td className="px-4 py-3 text-right font-mono">{eur(r.unit_price)}</td>
                  <td className={`px-4 py-3 text-right font-mono font-semibold ${isReturn ? "text-amber-700" : ""}`}>{eur(r.line_total)}</td>
                </tr>
              );})}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-neutral-50 border-t-2 border-neutral-300">
                <tr className="font-semibold">
                  <td className="px-4 py-3" colSpan="6">
                    {data.rows.length} di {data.total_entries} movimenti caricati
                    {hasCol && " · filtri di colonna attivi"}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{displayTotal.qty}</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right font-mono text-[#002FA7]" data-testid="ps-footer-total">{eur(displayTotal.amount)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {data.rows.length < data.total_entries && (
          <div className="flex justify-center">
            <button
              data-testid="ps-load-more"
              onClick={loadMore}
              disabled={loadingMore}
              className="text-sm px-6 py-2.5 bg-white border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 disabled:opacity-60 transition-colors"
            >
              {loadingMore ? "Caricamento…" : `Carica altri ${Math.min(PAGE_SIZE, data.total_entries - data.rows.length)}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 label-eyebrow bg-neutral-50 ${className}`}>
    <span className={`inline-flex items-center gap-1 ${className.includes("text-right") ? "justify-end" : ""}`}>{children}</span>
  </th>
);

const Stat = ({ label, value, strong, testid }) => (
  <div className="bg-white border border-neutral-200 p-4">
    <div className="label-eyebrow mb-1">{label}</div>
    <div data-testid={testid} className={`font-mono ${strong ? "text-xl md:text-2xl font-bold text-[#002FA7]" : "text-lg font-semibold"}`}>{value}</div>
  </div>
);
